<template>
  <div
    class="sidebar-shell"
    :class="{ 'sidebar-shell--open': isPanelOpen }"
    :style="{
      background: theme.background,
      color: theme.text,
      '--sidebar-panel-width': `${panelWidth}px`,
      '--sidebar-total-width': `${totalSidebarWidth}px`
    }"
  >
    <!-- ====================================================== -->
    <!-- LEVEL 1: TOP-LEVEL ICON RAIL -->
    <!-- ====================================================== -->

    <v-card
      flat
      class="sidebar-rail"
      :style="{
        background: theme.primary,
        color: theme.text
      }"
    >
      <v-list
        nav
        density="compact"
        class="sidebar-rail-list"
        :style="{
          background: theme.primary,
          color: theme.text
        }"
      >
        <template
          v-for="item in visibleRootMenu"
          :key="item.id"
        >
          <v-tooltip
            location="right"
            :text="item.title"
          >
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                class="sidebar-rail-item"
                :class="{
                  'sidebar-rail-item--active':
                    selectedRootId === item.id
                }"
                :active="selectedRootId === item.id"
                @click="selectRootItem(item)"
              >
                <template #default>
                  <div class="d-flex justify-center align-center w-100">
                    <v-icon
                      :icon="item.icon || 'mdi-circle-outline'"
                      size="24"
                    />
                  </div>
                </template>
              </v-list-item>
            </template>
          </v-tooltip>
        </template>
      </v-list>
    </v-card>

    <!-- ====================================================== -->
    <!-- LEVEL 2: SLIDING CHILD MENU -->
    <!-- ====================================================== -->

    <v-slide-x-transition>
      <v-card
        v-if="isPanelOpen && selectedRoot"
        flat
        class="sidebar-panel"
        :style="{
          background: theme.background,
          color: theme.text,
          width: `${panelWidth}px`,
          minWidth: `${panelWidth}px`,
          maxWidth: `${panelWidth}px`
        }"
      >
        <!-- PANEL HEADER -->
        <div
          class="sidebar-panel-header"
          :style="{
            background: theme.primary,
            color: theme.text
          }"
        >
          <div class="d-flex align-center ga-2 min-width-0">
            <v-icon
              :icon="selectedRoot.icon || 'mdi-circle-outline'"
              size="22"
            />

            <div class="sidebar-panel-title">
              {{ selectedRoot.title }}
            </div>
          </div>

          <v-tooltip
            location="bottom"
            :text="
              currentLanguage === 'fr'
                ? 'Fermer le menu'
                : 'Close menu'
            "
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-chevron-left"
                variant="text"
                size="small"
                @click="closePanel"
              />
            </template>
          </v-tooltip>
        </div>

        <!-- SEARCH -->
        <div class="sidebar-search">
          <v-text-field
            v-model="search"
            :label="t('search')"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
          />
        </div>

        <v-divider />

        <!-- CHILD MENU -->
        <div class="sidebar-panel-content">
          <v-list
            v-model:opened="open"
            nav
            density="compact"
            class="sidebar-child-list"
            :style="{
              background: theme.background,
              color: theme.text
            }"
          >
            <template
              v-for="child in filteredSelectedChildren"
              :key="child.id"
            >
              <!-- CHILD GROUP -->
              <v-list-group
                v-if="child.children?.length"
                :value="child.id"
              >
                <template #activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    :prepend-icon="
                      child.icon ||
                      'mdi-folder-outline'
                    "
                    :title="child.title"
                    class="sidebar-group-item"
                    :class="{
                      'sidebar-group-item--active':
                        containsActiveRoute(child)
                    }"
                  />
                </template>

                <!-- GRANDCHILDREN -->
                <template
                  v-for="sub in child.children"
                  :key="sub.id"
                >
                  <!-- SUPPORT AN EXTRA NESTED LEVEL -->
                  <v-list-group
                    v-if="sub.children?.length"
                    :value="sub.id"
                  >
                    <template #activator="{ props }">
                      <v-list-item
                        v-bind="props"
                        :prepend-icon="
                          sub.icon ||
                          'mdi-folder-outline'
                        "
                        :title="sub.title"
                        class="sidebar-subgroup-item"
                        :class="{
                          'sidebar-group-item--active':
                            containsActiveRoute(sub)
                        }"
                      />
                    </template>

                    <v-list-item
                      v-for="leaf in sub.children"
                      :key="leaf.id"
                      :prepend-icon="
                        leaf.icon ||
                        'mdi-circle-small'
                      "
                      :title="leaf.title"
                      link
                      :to="leaf.to"
                      :class="{
                        'active-item':
                          isRouteActive(leaf.to)
                      }"
                    />
                  </v-list-group>

                  <!-- NORMAL GRANDCHILD -->
                  <v-list-item
                    v-else
                    :prepend-icon="
                      sub.icon ||
                      'mdi-circle-small'
                    "
                    :title="sub.title"
                    link
                    :to="sub.to"
                    :class="{
                      'active-item':
                        isRouteActive(sub.to)
                    }"
                  />
                </template>
              </v-list-group>

              <!-- NORMAL CHILD -->
              <v-list-item
                v-else
                :prepend-icon="
                  child.icon ||
                  'mdi-circle-small'
                "
                :title="child.title"
                link
                :to="child.to"
                :class="{
                  'active-item':
                    isRouteActive(child.to)
                }"
              />
            </template>

            <div
              v-if="filteredSelectedChildren.length === 0"
              class="sidebar-empty"
            >
              <v-icon
                icon="mdi-magnify-close"
                size="28"
                class="mb-2"
              />

              <div>
                {{
                  currentLanguage === 'fr'
                    ? 'Aucun menu trouvé'
                    : 'No menu items found'
                }}
              </div>
            </div>
          </v-list>
        </div>

        <!-- ================================================== -->
        <!-- RESIZE HANDLE -->
        <!-- ================================================== -->

        <div
          class="sidebar-resize-handle"
          :class="{
            'sidebar-resize-handle--active':
              isResizing
          }"
          @pointerdown="startResize"
        >
          <div class="sidebar-resize-grip" />
        </div>
      </v-card>
    </v-slide-x-transition>
  </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useAuthStore } from "@/store/auth"
import { useI18n } from 'vue-i18n'

import { useLangStore } from '@/store/lang'
import { useThemeStore } from "@/store/auth"
import toast from 'vue-toastification'
const langStore = useLangStore()
const themeStore = useThemeStore()
const theme = computed(() => themeStore.themeStyles)
const lang = computed(() => langStore.currentLanguage)

const auth = useAuthStore()
const { t, locale } = useI18n()

const route = useRoute()
const router = useRouter()

const emit = defineEmits([
  'resize',
  'panel-state'
])

const isNbhostingAdmin = computed(() =>
  auth.loginName === 'nbhostingAdmin'
)
// SEARCH
const search = ref("")
const open = ref([])

watch(
  () => langStore.currentLanguage,
  (newLang) => {
    locale.value = newLang
    },
    { immediate: true }
  )
/* -------------------------------------------------------
   REACTIVE MENU (updates when lang changes)
------------------------------------------------------- */
const menu = computed(() => [
  {
    id: "Admin",
    title: t('administration'),
    icon: "mdi-shield-account",
    children: [
      {
        id: "Security/admin",
        title: 'Security',
        icon: "mdi-shield-lock-outline",
        children: [
          {
            id: "uc-2",
            title: t('permissionmanagement'),
            icon: "mdi-shield-account-outline",
            to: router.resolve({ name: "AdminPermissions", params: { lang: lang.value } }).href,
          },
          {
            id: "uc-3",
            title: t('rolesmanagement'),
            icon: "mdi-account-group-outline",
            to: router.resolve({ name: "AdminAssignRolePermissions", params: { lang: lang.value } }).href,
            public: true
          },
          { id: "subscription-4", title: 'Stripe configuration', icon: "mdi-credit-card-outline", to: router.resolve({ name: lang.value === 'en' ? "StripeConfigPage" : "StripeConfigPageFR"}).href },

        ]
      },
      {
        id: "Onboarding/admin",
        title: 'Onboarding',
        icon: "mdi-account-plus-outline",
        children: [
          {
            id: "uc-6",
            title: t('staffmanagement'),
            icon: "mdi-account-cog",
            to: router.resolve({ name: "StaffOnbording", params: { lang: lang.value } }).href,
          },
          {
            id: "uc-7",
            title: t('patientsmanagement'),
            icon: "mdi-account-edit-outline",
            to: router.resolve({ name: "PatientsOnbording", params: { lang: lang.value } }).href,
          },
        ]
      },
      {
        id: "Menusetup/admin",
        title: 'Menu setup',
        icon: "mdi-format-list-bulleted",
        children: [
          { id: "msetup-7", title: t('megacolumns'), icon: "mdi-view-column", to: router.resolve({ name: lang.value === 'en' ? "MegaColumnsSetupEN" : "MegaColumnsSetupFR"}).href },
          { id: "msetup-8", title: t('megaitems'), icon: "	mdi-format-list-bulleted-square", to: router.resolve({ name: lang.value === 'en' ? "MegaItemSetupEN" : "MegaItemSetupFR"}).href },
          { id: "msetup-9", title: t('megaimages'), icon: "mdi-image-multiple", to: router.resolve({ name: lang.value === 'en' ? "MegaImagesSetupEN" : "MegaImagesSetupFR"}).href },
          { id: "msetup-10", title: t('mainmenu'), icon: "mdi-menu-open", to: router.resolve({ name: lang.value === 'en' ? "MainMenuEN" : "MainMenuFR"}).href },
        ]
      },
      {
        id: "footersetup/admin",
        title: 'Footer setup',
        icon: "mdi-page-layout-foote",
        children: [
          { id: "msetup-4", 
            title: 'About footer',
            to: router.resolve({ name: lang.value === 'en' ?"footerAboutEN" : "footerAboutFR"}).href,
          },
          { id: "msetup-5", title: t('footercategories'), 
            icon: "mdi-page-layout-footer", 
            to: router.resolve({ name: lang.currentLanguage === 'en' ? "footerCategoriesEN" : "footerCategoriesAboutFR"}).href,
          },
          { id: "msetup-6", title: t('footerlinks'), icon: "mdi-link-variant", to: router.resolve({ name: lang.value === 'en' ? "footerLinksEN" : "footerLinksFR"}).href },
          { id: "msetup-6", title: t('convertvideo'), icon: "mdi-video-switch", to: router.resolve({ name: lang.value === 'en' ? "VideoConverterEN" : "VideoConverterFR"}).href },
        ]
      },
      {
        id: "PageDisplay/admin",
        title: 'Page, Display & SEO',
        icon: "mdi-page-layout-body",
        children: [
          { id: "pdcsec-9", title: t('displaysettings'), icon: "mdi-monitor-settings", to: router.resolve({ name: lang.value === 'en' ? "DisplaySettingsEN" : "DisplaySettingsFR"}).href },
          { id: "pdcsec-5", title: t('themesetup'), icon: "mdi-palette", to: router.resolve({ name: lang.value === 'en' ? "ThemeSetupEN" : "ThemeSetupFR"}).href },
          { id: "typographption-6", title: 'System Typogrpahy Configuration', icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "SystemTypographyConfigurationEN" : "SystemTypographyConfigurationFR"}).href },
          { id: "pdcsec-7", title: t('salutation'), icon: "mdi-hand-wave", to: router.resolve({ name: lang.value === 'en' ? "SalutationEN" : "SalutationFR"}).href },
          { id: "pdcsec-8", title: t('billingitems'), icon: "mdi-receipt-text", to: router.resolve({ name: lang.value === 'en' ? "BillingItemsEN" : "BillingItemsFR"}).href },
          { id: "pdosetup-3", title: 'Dynamic Page', icon: "mdi-image-area", to: router.resolve({ name: lang.value === 'en' ? "DynamicPageEN" : "DynamicPageFR"}).href },
          { id: "pdosetup-4", title: 'SEO Group and Meta', icon: "mdi-image-area", to: router.resolve({ name: lang.value === 'en' ? "SEOGroupsMetaAdminEN" : "SEOGroupsMetaAdminFR"}).href },
          
        ]
      },
      {
        id: "Banner/admin",
        title: 'Banners & Breadcrumb',
        children: [
          { id: "osetup-1", title: t('breadcrumbs'), icon: "mdi-dots-horizontal", to: router.resolve({ name: lang.value === 'en' ? "BreadCrumbsEN" : "BreadCrumbsFR"}).href },
          { id: "osetup-2", title: t('topbanners'), icon: "mdi-image-area", to: router.resolve({ name: lang.value === 'en' ? "topBannerEN" : "topBannerFR"}).href },
          
        ]
      },   

 
    ]
  },
  {
    id: "Operations",
    title: t('operationssetup'),
    icon: "mdi-account-circle",
    children: [
      {
        id: "Operations/Csec",
        title: t('componentsandsections'),
        children: [
          { id: "csec-3", title: t('listcaption'), icon: "mdi-format-title", to: router.resolve({ name: lang.value === 'en' ? "listCaptionEN" : "listCaptionFR"}).href },
          { id: "csec-4", title: t('listitems'), icon: "mdi-format-list-bulleted", to: router.resolve({ name: lang.value === 'en' ? "listItemEN" : "listItemFR"}).href },
          { id: "csec-6", title: t('independentlistitems'), icon: "mdi-format-list-checks", to: router.resolve({ name: lang.value === 'en' ? "independentListItemEN" : "independentListItemFR"}).href },
          { id: "csec-11", title: t('stepperheaders'), icon: "mdi-order-numeric-ascending", to: router.resolve({ name: lang.value === 'en' ? "steppersEN" : "steppersFR"}).href },
          { id: "csec-12", title: t('stepperbody'), icon: "mdi-view-sequential", to: router.resolve({ name: lang.value === 'en' ? "steppersBodyEN" : "steppersBodyFR"}).href },
          { id: "csec-7", title: t('years'), icon: "mdi-calendar-range", to: router.resolve({ name: lang.value === 'en' ? "yearsEN" : "yearsFR"}).href },
        ]
      },
      
        {
        id: "Operations/Clients",
        title: 'Membership & Insurance',
        children: [
          { id: "prsetup-1", title: t('patientsetup'), icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "PatientSetupEN" : "PatientSetupFR"}).href },
          { id: "prsetup-2", title: 'Insurance management', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "AllInsuranceEN" : "AllInsuranceFR"}).href },
          { id: "prsetup-3", title: 'Membership Tier', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "TenantMembershipTierEN" : "TenantMembershipTierFR"}).href },
          { id: "prsetup-4", title: 'Membership', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "TenantMembershipEN" : "TenantMembershipFR"}).href },
          { id: "prsetup-9", title: 'Loyalty program', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "LoyaltyProgramEN" : "LoyaltyProgramFR"}).href },
          
          { id: "prsetup-5", title: 'Insurance Partner', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "AllInsuranceEN" : "AllInsuranceFR"}).href },
          { id: "prsetup-6", title: 'Insurance Subscriber', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "UserInsuranceEN" : "UserInsuranceFR"}).href },
          { id: "prsetup-7", title: 'Insurance Dependent', icon: "mdi-clipboard-account", to: router.resolve({ name: lang.value === 'en' ? "UserInsuranceDependentEN" : "UserInsuranceDependentFR"}).href },
        ]
      },
       {
        id: "Operations/Policis",
        title: 'Policies & Messages',
        children: [
          { id: "oops-1", title: t('fqas'), icon: "mdi-frequently-asked-questions", to: router.resolve({ name: lang.value === 'en' ? "faAndAnsEN" : "faAndAnsFR"}).href },
          { id: "oops-2", title: t('policies'), icon: "mdi-shield-document", to: router.resolve({ name: lang.value === 'en' ? "termsPoliciesCookiesEN" : "termsPoliciesCookiesFR"}).href },
          { id: "oopspol-1", title: t('messagesettings'), icon: "mdi-message-cog", to: router.resolve({ name: lang.value === 'en' ? "messageSettingsEN" : "messageSettingsFR"}).href },

          
        //   { id: "oops-5", title: "Job applications", icon: "mdi-file-outline", to: router.resolve({ name: lang.currentLanguage === 'en' ? "jobApplicationEN" : "jobApplicationFR"}).href },
        ]
      },
      {
        id: "Operations/Csec",
        title: 'Organization',
        children: [
          { id: "corpsetup-3", title: t('corporatedirectory'), icon: "mdi-card-account-details", to: router.resolve({ name: lang.value === 'en' ? "corporateDirectoryEN" : "corporateDirectoryFR"}).href },
          { id: "corpsetup-4", title: t('leadershipcategories'), icon: "mdi-account-star", to: router.resolve({ name: lang.value === 'en' ? "leadershipCatEN" : "leadershipCatFR"}).href },
          { id: "corpsetup-4", title: t('leadershipprofiles'), icon: "mdi-account-tie", to: router.resolve({ name: lang.value === 'en' ? "leadershipProfilesEN" : "leadershipProfilesFR"}).href },
        ]
      },

      {
        id: "Operations/AI",
        title: t('aisearch'),
        children: [
          { id: "ai-1", title: t('uploadaisearchdata'), icon: "mdi-robot-search", to: router.resolve({ name: lang.value === 'en' ? "UploadAISearchEN" : "UploadAISearchFR"}).href },
          { id: "ai-2", title: t('previewaisearch'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "PreviewAISearchEN" : "PreviewAISearchFR"}).href },
          { id: "ai-3", title: t('createailist'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "CreateListAISearchEN" : "CreateListAISearchFR"}).href },
          { id: "ai-4", title: t('updateailist'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "CreateUpdateAISearchEN" : "CreateUpdateAISearchFR"}).href },
          { id: "aifq-1", title: t('fqchattopics'), icon: "mdi-chat-processing", to: router.resolve({ name: lang.value === 'en' ? "FQChatTopicsEN" : "FQChatTopicsFR"}).href },
          { id: "aifq-2", title: t('fqchatcategory'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "FQChatCategoryEN" : "FQChatCategoryFR"}).href },
          { id: "aifq-3", title: t('createfqchat'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "FQChatCreateUpdateEN" : "FQChatCreateUpdateFR"}).href },
          { id: "aifq-4", title: t('loginimages'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "LoginImagesEN" : "LoginImagesFR"}).href },
        ]
      },
    ]
  },

  {
    id: "VideosArticles",
    title: 'Videos & articles',
    icon: "mdi-account-circle",
    children: [
      {
        id: "VideosArticles/vvidcarousel",
        title: t('videosandcarousel'),
        children: [
          { id: "vc-1", title: t('carousels'), icon: "mdi-view-carousel", to: router.resolve({ name: lang.value === 'en' ? "carouselSlideshowEN" : "carouselSlideshowFR"}).href },
          { id: "vc-3", title: t('videoplayer'), icon: "mdi-play-circle", to: router.resolve({ name: lang.value === 'en' ? "videoPlayerEN" : "videoPlayerFR"}).href },
          { id: "vc-4", title: t('documentsupload'), icon: "mdi-file-upload", to: router.resolve({ name: lang.value === 'en' ? "genericImagesEN" : "genericImagesFR"}).href },
          { id: "vc-5", title: 'AI video studio', icon: "mdi-file-upload", to: router.resolve({ name: lang.value === 'en' ? "AIVideoStudioEN" : "AIVideoStudioFR"}).href },
        ]
      },
      {
        id: "VideosArticles/courses",
        title: 'Courses & topics',
        children: [
          { id: "vc-5", title: t('courses'), icon: "mdi-school", to: router.resolve({ name: lang.value === 'en' ? "courseGroupEN" : "courseGroupFR"}).href },
          
        ]
      },
      {
        id: "VideosArticles/articles",
        title: 'Blogs & articles',
        children: [
          { id: "amgmts-1", title: t('category'), icon: "mdi-tag-multiple", to: router.resolve({ name: lang.value === 'en' ? "BlogCategoryAdminEN" : "BlogCategoryAdminFR"}).href },
          { id: "amgmts-2", title: t('groups'), icon: "mdi-folder-account", to: router.resolve({ name: lang.value === 'en' ? "BlogGroupsAdminEN" : "BlogGroupsAdminFR"}).href },
          { id: "amgmts-3", title: t('topics'), icon: "mdi-book-open-variant", to: router.resolve({ name: lang.value === 'en' ? "BlogTopicsAdminEN" : "BlogTopicsAdminFR"}).href },
          { id: "amgmts-4", title: t('contributors'), icon: "mdi-account-multiple-plus", to: router.resolve({ name: lang.value === 'en' ? "BlogContributorsAdminEN" : "BlogContributorsAdminFR"}).href },
          { id: "amgmts-5", title: t('content'), icon: "mdi-file-document-edit", to: router.resolve({ name: lang.value === 'en' ? "BlogContentAdminEN" : "BlogContentAdminFR"}).href },
        ]
      },
       {
        id: "Articles/Arttenantid",
        title: t('tenanthelpsetup'),
        children: [
          { id: "amgmts-1", title: t('category'), icon: "mdi-tag-multiple", to: router.resolve({ name: lang.value === 'en' ? "BlogTenantCategoryAdminEN" : "BlogTenantCategoryAdminFR"}).href },
          { id: "amgmts-2", title: t('groups'), icon: "mdi-folder-account", to: router.resolve({ name: lang.value === 'en' ? "BlogTenantGroupsAdminEN" : "BlogTenantGroupsAdminFR"}).href },
          { id: "amgmts-3", title: t('topics'), icon: "mdi-book-open-variant", to: router.resolve({ name: lang.value === 'en' ? "BlogTenantTopicsAdminEN" : "BlogTenantTopicsAdminFR"}).href },
          { id: "amgmts-4", title: t('contributors'), icon: "mdi-account-multiple-plus", to: router.resolve({ name: lang.value === 'en' ? "BlogTenantContributorsAdminEN" : "BlogTenantContributorsAdminFR"}).href },
          { id: "amgmts-5", title: t('content'), icon: "mdi-file-document-edit", to: router.resolve({ name: lang.value === 'en' ? "BlogTenantContentAdminEN" : "BlogTenantContentAdminFR"}).href },
        ]
      },
       {
        id: "Articles/help",
        title: "Help-view",
        children: [
          { id: "hasecful-1", title: t('help'), icon: "mdi-help-circle", to: router.resolve({ name: lang.value === 'en' ? "GlobalTenantHelpCenterEN" : "GlobalTenantHelpCenterFR"}).href },
        ]
      }
       
    ]
  },

  {
    id: "MeetingMgmt",
    title: t('meetingmanagement'),
    icon: "mdi-account-circle",
    children: [
      {
        id: "MeetingMgmt/MCvid",
        title: t('setup'),
        children: [
          { id: "meets-1", title: t('organisation'), icon: "mdi-sitemap", to: router.resolve({ name: lang.value === 'en' ? "OrganizationEN" : "OrganizationFR"}).href },
          { id: "meets-2", title: t('staff'), icon: "mdi-account-group", to: router.resolve({ name: lang.value === 'en' ? "StaffEN" : "StaffFR"}).href },
          { id: "meets-3", title: t('availability'), icon: "mdi-clock-check", to: router.resolve({ name: lang.value === 'en' ? "AvailabilityEN" : "AvailabilityFR"}).href },
          { id: "meets-4", title: t('eventtype'), icon: "mdi-calendar-star", to: router.resolve({ name: lang.value === 'en' ? "EventTypeEN" : "EventTypeFR"}).href },
          { id: "meets-5", title: t('slotsgeneration'), icon: "mdi-timetable", to: router.resolve({ name: lang.value === 'en' ? "SlotGenerationEN" : "SlotGenerationFR"}).href },
        ]
      },
      {
        id: "MeetingMgmt/RCsec",
        title: t('bookings'),
        children: [
          { id: "mbooking-1", title: t('bookappointment'), icon: "mdi-calendar-edit", to: router.resolve({ name: lang.value === 'en' ? "BookAppointmentEN" : "BookAppointmentFR"}).href },
          { id: "mbooking-2", title: t('calendarview'), icon: "mdi-calendar-month", to: router.resolve({ name: lang.value === 'en' ? "CalendarViewEN" : "CalendarViewFR"}).href },
          { id: "mbooking-3", title: t('reschedule'), icon: "mdi-calendar-refresh", to: router.resolve({ name: lang.value === 'en' ? "RescheduleAppointmentEN" : "RescheduleAppointmentFR"}).href },
        ]
      }
    ]
  },
  {
    id: "ReportMgmt",
    title: 'Report Mgmt',
    icon: "mdi-account-circle",
    children: [
      {
        id: "ReportMgmt/RCvid",
        title: 'Report setup',
        children: [
          { id: "roops-4", title: 'Report Definition', icon: "mdi-view-sequential", to: router.resolve({ name: lang.value === 'en' ? "ReportDefinitionEN" : "ReportDefinitionFR"}).href },
          { id: "roops-6", title: 'Report Query Definition', icon: "mdi-view-sequential", to: router.resolve({ name: lang.value === 'en' ? "ReportQueryDefinitionEN" : "ReportQueryDefinitionFR"}).href },
        ]
      },
      {
        id: "ReportMgmt/sync",
        title: 'Entity Sync',
        children: [
          { id: "roops-7", title: 'Edge nodes', icon: "mdi-view-sequential", to: router.resolve({ name: lang.value === 'en' ? "EdgeNodeEN" : "EdgeNodeFR"}).href },
          { id: "roops-8", title: 'Sync Entities', icon: "mdi-view-sequential", to: router.resolve({ name: lang.value === 'en' ? "SyncEntityEN" : "SyncEntityFR"}).href },
        ]
      },
    ]
  },

  {
    id: "HostingMgmt",
    title: t('hostingmanagement'),
    icon: "mdi-account-circle",
    children: [
      ...(isNbhostingAdmin.value
        ? [
            { id: "huc-9", title: 'Functional Permissions', icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "PermissionsEN" : "PermissionsFR"}).href },
            {
              id: 'hsuc-9',
              title: 'Component Catalog',
              icon: 'mdi-plus-outline',
              to: router.resolve({
                name:
                  lang.value === 'en'
                    ? 'ComponentCatalogAdminEN'
                    : 'ComponentCatalogAdminFR'
              }).href
            },
            {
              id: 'shuc-10',
              title: 'Component Mdi Icons',
              icon: 'mdi-plus-outline',
              to: router.resolve({
                name:
                  lang.value === 'en'
                    ? 'MdiIconCatalogAdminEN'
                    : 'MdiIconCatalogAdminFR'
              }).href
            },
          { id: "hsubscription-7", title: t('subscription CRUD'), icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "SubscriptionCRUDEN" : "SubscriptionCRUDFR"}).href },
          { id: "hsubscription-5", title: 'Stripe Hosting configuration', icon: "mdi-plus-outline", to: router.resolve({ name: lang.value === 'en' ? "StripeHostingConfigPage" : "StripeHostingConfigPageFR"}).href },
          ]
        : []),
    ]
  },
    ]
  )


/* -------------------------------------------------------
   LANGUAGE
------------------------------------------------------- */
const currentLanguage = computed(() =>
  langStore.currentLanguage || 'en'
)

/* -------------------------------------------------------
   TOP-LEVEL SELECTION
------------------------------------------------------- */
const selectedRootId = ref('Admin')
const isPanelOpen = ref(true)

/* -------------------------------------------------------
   RESIZABLE SECOND PANE
------------------------------------------------------- */
const ICON_RAIL_WIDTH = 64
const DEFAULT_PANEL_WIDTH = 280
const MIN_PANEL_WIDTH = 220
const MAX_PANEL_WIDTH = 520

const panelWidth =
  ref(DEFAULT_PANEL_WIDTH)

const isResizing =
  ref(false)

const resizeStartX =
  ref(0)

const resizeStartWidth =
  ref(DEFAULT_PANEL_WIDTH)

const totalSidebarWidth =
  computed(() =>
    isPanelOpen.value
      ? ICON_RAIL_WIDTH + panelWidth.value
      : ICON_RAIL_WIDTH
  )

function emitSidebarWidth() {
  const detail = {
    railWidth:
      ICON_RAIL_WIDTH,

    panelWidth:
      isPanelOpen.value
        ? panelWidth.value
        : 0,

    totalWidth:
      totalSidebarWidth.value,

    isOpen:
      isPanelOpen.value
  }

  emit(
    'resize',
    detail
  )

  emit(
    'panel-state',
    detail
  )

  window.dispatchEvent(
    new CustomEvent(
      'nexgbits-sidebar-resize',
      {
        detail
      }
    )
  )
}

function getPointerX(event) {
  return Number(event?.clientX ?? 0)
}

let activeResizeTarget =
  null

function startResize(event) {
  if (!isPanelOpen.value) {
    return
  }

  if (
    event.pointerType === 'mouse' &&
    event.button !== 0
  ) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  isResizing.value =
    true

  resizeStartX.value =
    getPointerX(event)

  resizeStartWidth.value =
    panelWidth.value

  activeResizeTarget =
    event.currentTarget

  activeResizeTarget
    ?.setPointerCapture?.(
      event.pointerId
    )

  document.body.classList.add(
    'nexgbits-sidebar-resizing'
  )

  window.addEventListener(
    'pointermove',
    handleResize
  )

  window.addEventListener(
    'pointerup',
    stopResize
  )

  window.addEventListener(
    'pointercancel',
    stopResize
  )
}

function handleResize(event) {
  if (!isResizing.value) {
    return
  }

  event.preventDefault()

  const currentX =
    getPointerX(event)

  const delta =
    currentX -
    resizeStartX.value

  panelWidth.value =
    Math.min(
      MAX_PANEL_WIDTH,
      Math.max(
        MIN_PANEL_WIDTH,
        resizeStartWidth.value +
          delta
      )
    )

  emitSidebarWidth()
}

function stopResize(event) {
  if (!isResizing.value) {
    return
  }

  isResizing.value =
    false

  if (
    activeResizeTarget &&
    event?.pointerId != null
  ) {
    try {
      activeResizeTarget
        .releasePointerCapture?.(
          event.pointerId
        )
    } catch {
      // Ignore if capture was already released.
    }
  }

  activeResizeTarget =
    null

  document.body.classList.remove(
    'nexgbits-sidebar-resizing'
  )

  window.removeEventListener(
    'pointermove',
    handleResize
  )

  window.removeEventListener(
    'pointerup',
    stopResize
  )

  window.removeEventListener(
    'pointercancel',
    stopResize
  )

  emitSidebarWidth()
}

/* -------------------------------------------------------
   PERMISSION FILTERING
------------------------------------------------------- */
function canAccessItem(item) {
  if (!item) return false

  if (item.public === true) {
    return true
  }

  if (
    item.permission &&
    !auth.hasPermission(item.permission)
  ) {
    return false
  }

  return true
}

function filterByAccess(items) {
  return (items || [])
    .map(item => {
      if (!canAccessItem(item)) {
        return null
      }

      if (item.children?.length) {
        const children =
          filterByAccess(item.children)

        if (children.length === 0) {
          return item.to
            ? { ...item, children: undefined }
            : null
        }

        return {
          ...item,
          children
        }
      }

      return item
    })
    .filter(Boolean)
}

/* -------------------------------------------------------
   ROOT MENU
------------------------------------------------------- */
const visibleRootMenu = computed(() =>
  filterByAccess(menu.value)
)

const selectedRoot = computed(() =>
  visibleRootMenu.value.find(
    item =>
      String(item.id) ===
      String(selectedRootId.value)
  ) || null
)

/* -------------------------------------------------------
   SEARCH CURRENT ROOT CHILDREN
------------------------------------------------------- */
function filterItems(items, query) {
  const q =
    String(query || '')
      .trim()
      .toLowerCase()

  return (items || [])
    .map(item => {
      if (!canAccessItem(item)) {
        return null
      }

      const title =
        String(item.title || '')
          .toLowerCase()

      const match =
        !q ||
        title.includes(q)

      if (item.children?.length) {
        const filteredChildren =
          filterItems(
            item.children,
            q
          )

        if (
          filteredChildren.length > 0
        ) {
          return {
            ...item,
            children: filteredChildren
          }
        }

        return match
          ? {
              ...item,
              children:
                filterByAccess(
                  item.children
                )
            }
          : null
      }

      return match
        ? item
        : null
    })
    .filter(Boolean)
}

const filteredSelectedChildren =
  computed(() => {
    if (!selectedRoot.value) {
      return []
    }

    return filterItems(
      selectedRoot.value.children || [],
      search.value
    )
  })

/* -------------------------------------------------------
   ROUTE HELPERS
------------------------------------------------------- */
function normalizePath(value) {
  if (!value) {
    return ''
  }

  try {
    return new URL(
      value,
      window.location.origin
    ).pathname.replace(/\/+$/, '')
  } catch {
    return String(value)
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '')
  }
}

function isRouteActive(to) {
  if (!to) {
    return false
  }

  return (
    normalizePath(route.path) ===
    normalizePath(to)
  )
}

function containsActiveRoute(item) {
  if (!item) {
    return false
  }

  if (
    item.to &&
    isRouteActive(item.to)
  ) {
    return true
  }

  return (
    item.children || []
  ).some(containsActiveRoute)
}

function findRootForCurrentRoute() {
  return visibleRootMenu.value.find(
    root =>
      containsActiveRoute(root)
  ) || null
}

/* -------------------------------------------------------
   OPEN GROUP IDS
------------------------------------------------------- */
function collectGroupIds(
  items,
  results = []
) {
  ;(items || []).forEach(item => {
    if (item.children?.length) {
      results.push(item.id)

      collectGroupIds(
        item.children,
        results
      )
    }
  })

  return results
}

function refreshOpenGroups() {
  open.value =
    collectGroupIds(
      filteredSelectedChildren.value
    )
}

/* -------------------------------------------------------
   ROOT ICON CLICK
------------------------------------------------------- */
async function selectRootItem(item) {
  if (!item) {
    return
  }

  if (!item.children?.length) {
    if (item.to) {
      await router.push(item.to)
    }

    return
  }

  if (
    selectedRootId.value === item.id &&
    isPanelOpen.value
  ) {
    closePanel()
    return
  }

  selectedRootId.value =
    item.id

  search.value =
    ''

  isPanelOpen.value =
    true

  refreshOpenGroups()

  emitSidebarWidth()
}

function closePanel() {
  isPanelOpen.value =
    false

  search.value =
    ''

  open.value =
    []

  emitSidebarWidth()
}

/* -------------------------------------------------------
   KEEP ACTIVE ROUTE'S ROOT SELECTED
------------------------------------------------------- */
watch(
  () => route.path,
  () => {
    const root =
      findRootForCurrentRoute()

    if (root) {
      selectedRootId.value =
        root.id

      isPanelOpen.value =
        true
    } else if (
      !selectedRootId.value
    ) {
      selectedRootId.value =
        'Admin'

      isPanelOpen.value =
        true
    }
  },
  {
    immediate: true
  }
)

/* -------------------------------------------------------
   SEARCH: EXPAND MATCHING GROUPS
------------------------------------------------------- */
watch(
  search,
  () => {
    refreshOpenGroups()
  }
)

/* -------------------------------------------------------
   MENU/LANGUAGE/PERMISSION CHANGES
------------------------------------------------------- */
watch(
  visibleRootMenu,
  roots => {
    if (
      roots.length &&
      !roots.some(
        root =>
          String(root.id) ===
          String(selectedRootId.value)
      )
    ) {
      selectedRootId.value =
        roots.some(
          root =>
            String(root.id) ===
            'Admin'
        )
          ? 'Admin'
          : roots[0].id
    }

    if (
      roots.length &&
      selectedRootId.value
    ) {
      isPanelOpen.value =
        true

      refreshOpenGroups()
    }
  },
  {
    immediate: true
  }
)

watch(
  visibleRootMenu,
  roots => {
    if (!roots.length) {
      selectedRootId.value =
        null

      isPanelOpen.value =
        false

      return
    }

    const selectedStillVisible =
      roots.some(
        root =>
          String(root.id) ===
          String(selectedRootId.value)
      )

    if (!selectedStillVisible) {
      const routeRoot =
        findRootForCurrentRoute()

      selectedRootId.value =
        routeRoot?.id ||
        (
          roots.some(
            root =>
              String(root.id) ===
              'Admin'
          )
            ? 'Admin'
            : roots[0]?.id
        )

      isPanelOpen.value =
        Boolean(
          selectedRootId.value
        )
    }
  }
)
/* -------------------------------------------------------
   RESIZE CLEANUP
------------------------------------------------------- */
onBeforeUnmount(() => {
  stopResize()

})

watch(
  totalSidebarWidth,
  () => {
    emitSidebarWidth()
  },
  {
    immediate: true
  }
)

</script>

<style scoped>
.sidebar-shell {
  display: flex;
  align-items: stretch;
  width: 64px;
  min-height: 100%;
  overflow: visible;
  transition: width 160ms ease;
}

.sidebar-shell--open {
  width: var(--sidebar-total-width);
}

/* ============================================================
   ICON RAIL
============================================================ */

.sidebar-rail {
  width: 64px;
  min-width: 64px;
  border-radius: 0;
  z-index: 2;
}

.sidebar-rail-list {
  padding: 8px 6px;
}

.sidebar-rail-item {
  width: 52px;
  min-height: 48px !important;
  margin: 4px 0;
  padding: 0 !important;
  border-radius: 10px !important;
  justify-content: center;
}

.sidebar-rail-item :deep(.v-list-item__content) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.sidebar-rail-item--active {
  background:
    rgba(255, 255, 255, 0.18) !important;
}

.sidebar-rail-item:hover {
  background:
    rgba(255, 255, 255, 0.12) !important;
}

/* ============================================================
   SLIDING PANEL
============================================================ */

.sidebar-panel {
  position: relative;
  flex: 0 0 auto;
  width: var(--sidebar-panel-width);
  min-width: var(--sidebar-panel-width);
  max-width: var(--sidebar-panel-width);
  border-radius: 0;
  border-right:
    1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    6px 0 18px rgba(0, 0, 0, 0.08);
  z-index: 1;
}

.sidebar-panel-header {
  min-height: 56px;
  padding: 8px 10px 8px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-panel-title {
  min-width: 0;
  max-width: 185px;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-search {
  padding: 12px;
}

.sidebar-panel-content {
  max-height: calc(100vh - 130px);
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-child-list {
  padding: 6px;
}

.sidebar-group-item,
.sidebar-subgroup-item,
.sidebar-child-list :deep(.v-list-item) {
  min-height: 38px !important;
  border-radius: 7px;
  margin-bottom: 2px;
}

.sidebar-group-item {
  font-weight: 600;
}

.sidebar-subgroup-item {
  font-weight: 500;
}

.sidebar-group-item--active {
  background:
    rgba(25, 118, 210, 0.10) !important;
}

/* ============================================================
   ACTIVE ROUTE
============================================================ */

.active-item {
  background:
    rgba(25, 118, 210, 0.16) !important;
  color: #1976d2 !important;
  font-weight: 700;
}

.active-item :deep(.v-icon) {
  color: #1976d2 !important;
}

/* ============================================================
   NESTING
============================================================ */

.sidebar-child-list
  :deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 24px !important;
}

.sidebar-child-list
  :deep(
    .v-list-group__items
    .v-list-group__items
    .v-list-item
  ) {
  padding-inline-start: 38px !important;
}

.sidebar-empty {
  padding: 32px 12px;
  text-align: center;
  opacity: 0.7;
  font-size: 14px;
}

.min-width-0 {
  min-width: 0;
}

/* ============================================================
   RESIZE HANDLE
============================================================ */

.sidebar-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 100%;
  cursor: ew-resize;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  touch-action: none;
}

.sidebar-resize-handle::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 3px;
  width: 2px;
  background:
    rgba(0, 0, 0, 0.08);
  transition:
    background 120ms ease,
    width 120ms ease;
}

.sidebar-resize-handle:hover::before,
.sidebar-resize-handle--active::before {
  width: 3px;
  background:
    rgba(25, 118, 210, 0.55);
}

.sidebar-resize-grip {
  width: 3px;
  height: 42px;
  border-radius: 999px;
  background:
    rgba(0, 0, 0, 0.18);
  opacity: 0;
  transition: opacity 120ms ease;
}

.sidebar-resize-handle:hover
.sidebar-resize-grip,
.sidebar-resize-handle--active
.sidebar-resize-grip {
  opacity: 1;
}

:global(body.nexgbits-sidebar-resizing) {
  cursor: ew-resize !important;
  user-select: none !important;
}

:global(body.nexgbits-sidebar-resizing *) {
  cursor: ew-resize !important;
}

:global(body.nexgbits-sidebar-resizing) .sidebar-shell {
  transition: none !important;
}


/* ============================================================
   SMALL VIEWPORTS
============================================================ */

@media (max-width: 600px) {
  .sidebar-shell--open {
    width: min(
      var(--sidebar-total-width),
      100vw
    );
  }

  .sidebar-panel {
    width: min(
      var(--sidebar-panel-width),
      calc(100vw - 64px)
    );
    min-width: min(
      var(--sidebar-panel-width),
      calc(100vw - 64px)
    );
    max-width: calc(100vw - 64px);
  }

  .sidebar-resize-handle {
    display: none;
  }
}
</style>
